import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import HelpIcon from '@material-ui/icons/HelpOutline';
import PropTypes from 'prop-types';
import React from 'react';
import { globalStyles } from '../core/style/gstheme';

const helpText = {
    "IMPORT_APPROACH": {
        "en": [
            {
                "text": [
                    "The classic Graphologi import approach is namespace based. That means that resources are associated with ontologies based on the IRIs of the ontologies in the import. If there are resources in the import in different namespaces they will not be imported. This approach is great for cleaning up data that has had fragments of other ontologies included in the file for 'completeness'.",
                    "The OWL specification is based on documents, not namespaces. When using document based import everything in that document is associated with any ontologies defined in that document. If owl:import is used resources will be associated with any of the documents in which they are referenced. If an ontology extends another namespace document based import will ensure that the extensions are imported and visible.",
                    "For many simple ontologies either approach will give the same outcome.",
                    "Please refer to the manual for more details."
                ]
            }
        ]
    },
    "LARGE_DATA": {
        "en": [
            {
                "text": [
                    "Where data is large certain actions may take a little longer, such as unfiltered advanced searches.",
                    "If you notice certain actions taking a long time, and you know that you have substantial amounts of data, you can disable certain features in Graphologi to improve responsiveness.",
                    "The list of features disabled is:",
                    "Unfiltered advanced searches (which includes searching in bulk editing).",
                    "Unfiltered searches for relationships when searching in a component."
                ]
            }
        ]
    },
    "MINOR_ISSUES": {
        "en": [
            {
                "text": [
                    "Minor errors are those where the data can safely be ignored or removed without affecting the result of the import.",
                    "You will be informed of any minor errors that have been found and we would recommend addressing these at the source of the data if possible.",
                    "Minor errors include:",
                    "IRIs on properties where only literals supported (Removed)",
                    "Literals on object properties. (Removed)",
                    "Unsupported literal values. (Removed)"
                ]
            }
        ]
    },
    "BULK_CHANGE": {
        "en": [
            {
                "text": [
                    "Bulk actions will add, remove or replace values for a set of resources.",
                    "Adding values will only add the value and update the history for those resources not already having the value.",
                    "Removing values will only remove the value and update the history for those resources that have the value.",
                    "When using 'Replace' all existing values for the property will be removed and the new value added. For language based properties only values in that language will be replaced.",
                    "When using 'Remove all' every value for a property will be removed."
                ]
            }
        ]
    },
    "SPARQL_SHORTCUTS": {
        "en": [
            {
                "text": [
                    "The following shortcut keys are available for the SPARQL editor (in addition to standard shortcuts)."
                ]
            },
            {
                "heading": "Ctrl + /",
                "text": [
                    "Comment/uncomment the current line or the lines in the current selction."
                ]
            },
            {
                "heading": "Ctrl + [ OR Cmd + [",
                "text": [
                    "Decrement the indentation for the current line or the lines in the current selection."
                ]
            },
            {
                "heading": "Ctrl + ] OR Cmd + ]",
                "text": [
                    "Increment the indentation for the current line or the lines in the current selection."
                ]
            },
            {
                "heading": "Ctrl + Shift + F",
                "text": [
                    "Format the lines in the current selection."
                ]
            },
            {
                "heading": "Shift + Tab",
                "text": [
                    "Automatically indents the current line or the lines in the current selection."
                ]
            }
        ]
    },
    "ADD_SUGGESTION": {
        "en": [
            {
                "text": [
                    "The following explains the types of suggestion supported by Graphologi.",
                    "Not all types will be available in all situations."
                ]
            },
            {
                "heading": "New Concept",
                "text": [
                    "Propose a new candidate concept for the taxonomy."
                ]
            },
            {
                "heading": "Alternative Label",
                "text": [
                    "Suggest an alternative label for a concept."
                ]
            },
            {
                "heading": "Deprecate",
                "text": [
                    "Suggest the deprecation of a concept that is no longer relevant and should not be used."
                ]
            },
            {
                "heading": "Move",
                "text": [
                    "Suggest moving a concept to another parent/broader concept in the taxonomy."
                ]
            },
            {
                "heading": "Preferred Label",
                "text": [
                    "Suggest a preferred label translation for a concept."
                ]
            },
            {
                "heading": "Remove",
                "text": [
                    "Suggest removing a concept from a broader parent."
                ]
            },
            {
                "heading": "Spelling",
                "text": [
                    "Flag a misspelled label or suggest an alternative spelling."
                ]
            },
            {
                "heading": "Split",
                "text": [
                    "When too many assets are tagged with a generic concept suggest splitting the concept."
                ]
            },
            {
                "heading": "Terminology",
                "text": [
                    "Suggest a different label to replace an antiquated or out-of-date label."
                ]
            }
        ]
    },
    "ADD_TOP_CONCEPT": {
        "en": [
            {
                "text": [
                    "A preferred label must be unique per language within a concept scheme.",
                    "If SKOS-XL is enabled you will have the option to create the label as a SKOS-XL label rather than a direct label on the concept.",
                    "If an unused SKOS-XL label is found you have the option to use this rather than create a new label."
                ]
            }
        ]
    },
    "ADD_CLASS": {
        "en": [
            {
                "text": [
                    "You will only be permitted to add classes that are available from the associated ontologies and that are not disjoint with any existing classes.",
                    "If the concept has narrower classes you will be given the option of adding the class to narrower concepts. If selected this will only apply to concepts that are in this scheme."
                ]
            }
        ]
    },
    "ADD_PREFIX": {
        "en": [
            {
                "text": [
                    "Prefixes can currently only contain the characters a-z or A-Z and can be at maximum twenty characters.",
                    "IRIs must end with a slash or a hash."
                ]
            }
        ]
    },
    "CHANGE_IRI": {
        "en": [
            {
                "text": [
                    "Changing the IRI of a resource can have many implications. Please make sure you are aware of the effects it may have.",
                    "When changing an IRI in Graphologi you have two options. The first option does not physically change the IRI but records the new IRI and that new IRI will be displayed and used in most places in Graphologi. Please see the documentation for full details.",
                    "It is also possible to make IRI changes permanent. Doing this does physically change the data throughout the project. That is, any resources with edited IRIs will be altered permanently and any resources linked to them will have values changes also. This option will lock the project due to the potential impact."
                ]
            }
        ]
    },
    "FIND_RESOURCE": {
        "en": [
            {
                "text": [
                    "You can add resources from within the current project by selecting that option and searching.",
                    "Additionally, you can also use the current project option to enter external IRIs and then click the Add button.",
                    "The linked projects option, if available, allows you to search any projects linked to the taxonomy.",
                    "If it is enabled you will be able to search DBpedia resources by selecting that option. Note that this search is only in English."
                ]
            }
        ]
    },
    "PREF_LABEL": {
        "en": [
            {
                "text": [
                    "A preferred label must be unique per language within a concept scheme."
                ]
            }
        ]
    },
    "ALT_LABEL": {
        "en": [
            {
                "text": [
                    "An alternative label must be unique compared to any preferred or hidden label in the same language."
                ]
            }
        ]
    },
    "HIDDEN_LABEL": {
        "en": [
            {
                "text": [
                    "A hidden label must be unique compared to any preferred or alternative label in the same language."
                ]
            }
        ]
    },
    "NOTATION": {
        "en": [
            {
                "text": [
                    "A notation should be unique across a concept scheme.",
                    "You may also, optionally, select a datatype for the notation. If a datatype is selected the notation will be checked to ensure it is valid for that datatype."
                ]
            }
        ]
    },
    "IRI": {
        "en": [
            {
                "text": [
                    "An IRI is similar to a URI or a URL. It is a used to create the identifiers for resources.",
                    "The IRI you enter should be of the form http:// or https://",
                    "For example it might be:",
                    "https://schema.example.org/subject",
                    "All IRIs generated by the system will use this as the basis for resources.",
                    "An IRI generated for the example above might be:",
                    "https://schema.example.org/subject/Medicine",
                    "for a resource that has a label of 'Medicine' and where the IRI Format is set as Label."
                ]
            }
        ]
    }
};

const styles = theme => {
    return Object.assign({
        options: {
            display: 'block',
            clear: 'both',
            overflow: 'auto',
            marginBottom: 6
        },
        optionHeading: {
            display: 'block',
            float: 'left',
            width: '30%'
        },
        optionText: {
            display: 'block',
            float: 'right',
            width: '60%'
        },
        textLine: {
            display: 'block',
            marginBottom: 6
        },
        help: {
            position: 'absolute',
            color: theme.palette.primary.main,
            right: '25px',
            top: '20px'
        },
        helpNoPosition: {
            marginTop: '5px'
        },
        text: {
            padding: '12px 12px 6px 12px',
            fontWeight: 400
        },
        arrow: {
            color: theme.palette.primary.light
        }
    }, globalStyles);
};


class Help extends React.Component {

    render() {

        const { classes, textRef, position } = this.props;
        const help = helpText[textRef] && helpText[textRef]["en"] ? helpText[textRef]["en"] : null;
        const text = help ?
            <React.Fragment>
                <span data-test="help-text">
                    {
                        help.map((item, i) =>
                            item.heading ?
                                <span key={i} className={classes.options}>
                                    <span className={classes.optionHeading}>
                                        {item.heading}
                                    </span>
                                    <span className={classes.optionText}>
                                        {
                                            item.text.map((line, i) => <p style={{ margin: 0 }} key={i}>{line}</p>)
                                        }
                                    </span>
                                </span>
                                :
                                item.text.map((line, i) => <span className={classes.textLine} key={i}>{line}</span>))
                    }
                </span>
            </React.Fragment>
            :
            "Help not available";

        return (
            <Tooltip
                data-test="help-info"
                arrow
                placement="bottom-end"
                title={text}
                classes={{
                    tooltip: classes.text,
                    arrow: classes.arrow
                }}>
                <HelpIcon className={position ? classes.help : classes.helpNoPosition} />
            </Tooltip>
        );
    }
}

Help.defaultProps = {
    position: true
};

Help.propTypes = {
    textRef: PropTypes.string.isRequired,
    classes: PropTypes.object,
    position: PropTypes.bool
};

export default withStyles(styles)(Help);